/**
 * Event Sessions.
 *
 * @format
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import VideoModal from './video-modal/video-modal';
import ModalLink from './modal-link/modal-link';
import SimpleEventModal from './event-page-modals/simple-event-modal/simple-event-modal';
import FullEventModal from './event-page-modals/full-event-modal/full-event-modal';

/**
 * Load Event Sessions.
 */
export const modals = (): void => {
  /**
   * Build Simple Modal Links.
   *
   * @param {NodeListOf<HTMLElement} modalLinks
   */
  const buildSimpleModalLinks = (modalLinks: NodeListOf<HTMLElement>): void => {
    if (modalLinks.length > 0) {
      Array.prototype.forEach.call(modalLinks, (link: Element) => {
        const content: HTMLElement | null = link.querySelector(
          '.modal-link-content'
        );
        const linkClasses: HTMLElement | null = link.querySelector(
          '.modal-link-classes'
        );
        const title: HTMLElement | null =
          link.querySelector('.modal-link-title');
        const linkText: HTMLElement | null =
          link.querySelector('.modal-link-text');

        if (title && linkText && content) {
          const simpleModalLinkProps = {
            title: title?.innerText,
            linkText: linkText.innerHTML,
            content: content.innerHTML,
            modalLinkClasses: linkClasses?.innerText || '',
          };
          const simpleModalLink = React.createElement(
            ModalLink,
            simpleModalLinkProps,
            null
          );
          const root = createRoot(link);
          root.render(simpleModalLink);
        }
      });
    }
  };

  /**
   * Build Video Modals.
   *
   * @param {NodeListOf<HTMLElement} videos
   */
  const buildVideoModals = (videos: NodeListOf<HTMLElement>): void => {
    if (videos.length > 0) {
      Array.prototype.forEach.call(videos, (video: Element) => {
        const title: HTMLElement | null = video.querySelector('.video-title');
        const description: HTMLElement | null =
          video.querySelector('.video-description');
        const linkText: HTMLElement | null =
          video.querySelector('.video-link-text');
        const videoId: HTMLElement | null = video.querySelector('.video-id');
        const linkType: HTMLElement | null =
          video.querySelector('.video-link-type');
        const videoClasses: HTMLElement | null = video.querySelector(
          '.video-link-classes'
        );

        const videoModalClasses: string = videoClasses?.innerText || '';

        if (title && linkText && videoId && linkType) {
          const videoModalProps = {
            title: title.innerText,
            description: description?.innerHTML || '',
            linkText: linkText.innerText,
            videoId: videoId.innerText,
            linkType: linkType.innerText,
            videoClasses: videoModalClasses,
            videoParent: video.parentElement,
            videoModalId: video.id,
          };
          const reactVideoModal = React.createElement(
            VideoModal,
            videoModalProps,
            null
          );
          const root = createRoot(video);
          root.render(reactVideoModal);
        }
      });
    }
  };

  /**
   * Add Session Modal.
   */
  const addSessionModal = (modalType: JSX.Element): void => {
    const eventModalContainer: Element = document.createElement('div');
    eventModalContainer.id = 'session-container';
    document.body.appendChild(eventModalContainer);
    const root = createRoot(eventModalContainer);
    root.render(React.createElement(modalType));
  };

  /**
   * Build Modals.
   */
  const buildModals = (): void => {
    /**
     * Simple Modal Links.
     */
    const simpleModalLinks: NodeListOf<HTMLElement> =
      document.querySelectorAll('.simple-modal-link');
    if (simpleModalLinks.length > 0) {
      buildSimpleModalLinks(simpleModalLinks);
    }

    /**
     * Video Modal.
     */
    const videos: NodeListOf<HTMLElement> =
      document.querySelectorAll('.video-modal-link');
    if (videos.length > 0) {
      buildVideoModals(videos);
    }

    /**
     * Event Modal Groups.
     */
    const sessionPage: Element | null =
      document.querySelector('.event-sessions');
    if (sessionPage) {
      if (sessionPage.querySelector('.event-card')) {
        addSessionModal(SimpleEventModal);
      }

      if (sessionPage.querySelector('.full-modal-event-card')) {
        addSessionModal(FullEventModal);
      }
    }
  };
  buildModals();
};
